//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';

import DefaultModal from '@components/DefaultModal';
import { DangerButtonStyles } from '@components/atoms/button/buttonStyles';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { BodyText, TitleText } from '@components/atoms/text/Text';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { useDeleteLabelMutation } from '@content-drawer/filters/labels/hooks/useDeleteLabelMutation';
import { LabelInputMatch } from '@content-drawer/filters/labels/utils/LabelInputMatch';
import { pluralize } from '@utils/stringUtils';

export interface LabelDeleteRequest {
  count: number;
  label: string;
}

export interface LabelDeleteConfirmationDialogProps {
  isOpen: boolean;
  onCanceled: () => void;
  onCompleted: () => void;
  request: LabelDeleteRequest;
}

export const LabelDeleteConfirmationDialog = ({
  isOpen,
  onCanceled,
  onCompleted,
  request,
}: LabelDeleteConfirmationDialogProps) => {
  const [isNameMatching, setIsNameMatching] = useState(false);
  const { count, label } = request;

  const { isLoading, mutate } = useDeleteLabelMutation();

  return (
    <DefaultModal
      closeModal={onCanceled}
      contents={
        <Body>
          <BodyText>
            Are you sure you want to delete the label{' '}
            <StyledText>"{label}"</StyledText>? This label will be permanently
            removed from {pluralize(count, 'item')}. If this label is used to
            filter content in Galleries on your website, those experiences may
            fail.
          </BodyText>

          <LabelInputMatch
            label={label}
            labelAction='delete'
            onCancel={onCanceled}
            onComplete={() => {
              onCompleted();
              mutate(label);
            }}
            setIsNameMatching={setIsNameMatching}
          />
          {isLoading && <CenteredSpinner />}
        </Body>
      }
      isModalOpen={isOpen}
      primaryButtonInfo={{
        buttonContent: 'Delete Label',
        buttonStyles: DangerButtonStyles,
        isDisabled: !isNameMatching || isLoading,
        onClick: () => {
          onCompleted();
          mutate(label);
        },
        trackingId: ContentDrawerTrackingIds.DELETE_LABEL_CONFIRMATION,
      }}
      secondaryButtonInfo={{
        buttonContent: 'Cancel',
        onClick: onCanceled,
        trackingId: ContentDrawerTrackingIds.CLOSE_DELETE_LABEL_CONFIRMATION,
      }}
      shouldHideOverlay={true}
      title={<Title>Delete label</Title>}
      width='wide'
    />
  );
};

const Body = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
  `,
);

const Title = styled(TitleText)(
  ({ theme }) => `
    display: flex;
    flex-wrap: wrap;
    max-width: ${theme.wideMeasure};
  `,
);

const StyledText = styled.span(
  ({ theme }) => `
  display: inline;
  width: fit-content;
  font-weight: ${theme.fontWeights.bold};
`,
);
